import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, Facebook, MapPin } from 'lucide-react';
import { siteConfig } from '../data/mock';
import { resolveMediaUrl } from '../utils/media';

/**
 * BeforeAfter - Before/After image gallery
 * Used on: Homepage, Service pages
 * 
 * Since we don't have real images yet, we'll use placeholder data
 * that can be easily replaced with real images later
 */

// Real before/after photos from completed jobs
const beforeAfterData = [
  {
    id: 1,
    location: 'Brétignolles-sur-Mer',
    type: 'Désherbage + paillage massif',
    // Composite image - shows before/after side by side
    compositeImage: 'https://customer-assets.emergentagent.com/job_0fac0737-5b04-4406-9bac-fa0bfebc2359/artifacts/ci5p3h2j_avant_apres_zone_2.jpg',
    isComposite: true
  },
  {
    id: 2,
    location: 'Brétignolles-sur-Mer',
    type: 'Entretien jardin + bordures',
    // Composite image - shows before/after side by side
    compositeImage: 'https://customer-assets.emergentagent.com/job_0fac0737-5b04-4406-9bac-fa0bfebc2359/artifacts/txyai05t_avant_apres_zone_1.jpg',
    isComposite: true
  }
];

// Composite card for images that already show before/after side by side
const CompositeBeforeAfterCard = ({ item, onClick }) => {
  return (
    <div
      className="group cursor-pointer rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 bg-white border border-gray-100"
      onClick={() => onClick(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick(item)}
      data-testid="home-realisation-card"
    >
      {/* Image: fixed aspect, contained on a soft background so both cards align neatly and nothing is cropped */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-emerald-50 to-gray-100 overflow-hidden">
        <img
          src={item.compositeImage}
          alt={`Avant/Après - ${item.type}${item.location ? ` à ${item.location}` : ''}`}
          className="absolute inset-0 w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
          loading="lazy"
        />
      </div>

      {/* Caption */}
      <div className="p-4">
        <h3 className="font-bold text-gray-900 leading-snug line-clamp-1">{item.type}</h3>
        {item.location && (
          <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
            <MapPin className="w-4 h-4 text-emerald-600" />
            {item.location}
          </p>
        )}
      </div>
    </div>
  );
};

const BeforeAfterCard = ({ item, onClick }) => {
  const [showAfter, setShowAfter] = useState(false);

  // Use composite card for composite images
  if (item.isComposite) {
    return <CompositeBeforeAfterCard item={item} onClick={onClick} />;
  }

  return (
    <div 
      className="relative group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all"
      onMouseEnter={() => setShowAfter(true)}
      onMouseLeave={() => setShowAfter(false)}
      onClick={() => onClick(item)}
    >
      {/* Before Image */}
      <img
        src={item.before}
        alt={`Avant - ${item.type} à ${item.location}`}
        className={`w-full h-48 object-cover transition-opacity duration-300 ${showAfter ? 'opacity-0' : 'opacity-100'}`}
        loading="lazy"
      />
      
      {/* After Image (overlay) */}
      <img
        src={item.after}
        alt={`Après - ${item.type} à ${item.location}`}
        className={`absolute inset-0 w-full h-48 object-cover transition-opacity duration-300 ${showAfter ? 'opacity-100' : 'opacity-0'}`}
        loading="lazy"
      />
      
      {/* Label */}
      <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold text-white ${showAfter ? 'bg-emerald-600' : 'bg-gray-800'}`}>
        {showAfter ? 'APRÈS' : 'AVANT'}
      </div>
      
      {/* Caption */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
        <p className="text-white text-sm font-medium">{item.location}</p>
        <p className="text-white/80 text-xs">{item.type}</p>
      </div>
      
      {/* Hover instruction */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="text-white text-sm font-medium bg-black/50 px-3 py-1 rounded">
          {showAfter ? 'Voir avant' : 'Survolez pour voir après'}
        </span>
      </div>
    </div>
  );
};

const BeforeAfter = ({ variant = 'full', maxItems = 6 }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [showAfterInLightbox, setShowAfterInLightbox] = useState(false);
  const [dbItems, setDbItems] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Fetch admin-managed featured gallery items; fall back to hardcoded data.
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await fetch(`${API}/api/gallery?featured_only=true&limit=${maxItems}`);
        if (res.ok) {
          const data = await res.json();
          const normalized = data.map((item) => ({
            id: item.id,
            location: item.location || '',
            type: item.title || '',
            description: item.description,
            compositeImage: resolveMediaUrl(item.composite_image || item.after_image),
            before: resolveMediaUrl(item.before_image),
            after: resolveMediaUrl(item.after_image),
            isComposite: !!item.composite_image
          }));
          if (normalized.length > 0) setDbItems(normalized);
        }
      } catch (e) {
        // silent — fall back to hardcoded data
      }
    };
    fetchFeatured();
  }, [API, maxItems]);

  const items = (dbItems || beforeAfterData).slice(0, maxItems);

  const scrollRef = useRef(null);
  const scrollByDir = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  const openLightbox = (item) => {
    setCurrentItem(item);
    setShowAfterInLightbox(false);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentItem(null);
  };

  const navigateLightbox = (direction) => {
    const currentIndex = items.findIndex(i => i.id === currentItem.id);
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % items.length;
    } else {
      newIndex = (currentIndex - 1 + items.length) % items.length;
    }
    setCurrentItem(items[newIndex]);
    setShowAfterInLightbox(false);
  };

  // Compact variant for service pages (3 items strip)
  if (variant === 'compact') {
    return (
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
          Avant / Après
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {items.slice(0, 3).map((item) => (
            <BeforeAfterCard key={item.id} item={item} onClick={openLightbox} />
          ))}
        </div>
      </div>
    );
  }

  // Full variant for homepage
  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 text-center">
          Mes réalisations
        </h2>
        <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
          Avant / après de chantiers récents chez mes clients en Vendée. Cliquez pour agrandir.
        </p>

        {/* Horizontal carousel — compact, shows 1 (mobile) / 2 (tablet) / 3 (desktop) at a time */}
        <div className="relative max-w-6xl mx-auto">
          <button
            onClick={() => scrollByDir(-1)}
            aria-label="Réalisations précédentes"
            data-testid="realisations-prev"
            className="hidden sm:flex items-center justify-center absolute -left-3 lg:-left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 text-gray-700 hover:text-emerald-600 hover:shadow-xl transition-all"
          >
            <ChevronLeft size={22} />
          </button>

          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-4 px-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            data-testid="realisations-carousel"
          >
            {items.map((item) => (
              <div
                key={item.id}
                className="snap-start shrink-0 w-[85%] sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]"
              >
                <BeforeAfterCard item={item} onClick={openLightbox} />
              </div>
            ))}
          </div>

          <button
            onClick={() => scrollByDir(1)}
            aria-label="Réalisations suivantes"
            data-testid="realisations-next"
            className="hidden sm:flex items-center justify-center absolute -right-3 lg:-right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 text-gray-700 hover:text-emerald-600 hover:shadow-xl transition-all"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="text-center mt-10">
          <p className="text-sm text-gray-500 mb-4">
            Retrouvez toutes mes réalisations sur Facebook
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={siteConfig.contact.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#1877F2] hover:bg-[#0f64d6] text-white font-semibold px-6 py-3 rounded-xl transition-colors"
              data-testid="home-facebook-btn"
            >
              <Facebook className="w-4 h-4" />
              Suivez-moi sur Facebook
            </a>
          </div>
        </div>
      </div>

      {/* Lightbox for composite images */}
      {lightboxOpen && currentItem && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          >
            <X size={32} />
          </button>
          
          {items.length > 1 && (
            <>
              <button 
                onClick={() => navigateLightbox('prev')}
                className="absolute left-4 text-white hover:text-gray-300 transition-colors z-10"
              >
                <ChevronLeft size={48} />
              </button>
              
              <button 
                onClick={() => navigateLightbox('next')}
                className="absolute right-4 text-white hover:text-gray-300 transition-colors z-10"
              >
                <ChevronRight size={48} />
              </button>
            </>
          )}
          
          <div className="max-w-5xl w-full">
            <div className="relative">
              {currentItem.isComposite ? (
                <img
                  src={currentItem.compositeImage}
                  alt={`Avant/Après - ${currentItem.type} à ${currentItem.location}`}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                />
              ) : (
                <img
                  src={showAfterInLightbox ? currentItem.after : currentItem.before}
                  alt={`${showAfterInLightbox ? 'Après' : 'Avant'} - ${currentItem.type}`}
                  className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                />
              )}
            </div>
            
            <div className="text-center mt-4">
              <p className="text-white text-lg font-medium">{currentItem.location}</p>
              <p className="text-white/70 text-base">{currentItem.type}</p>
              {!currentItem.isComposite && (
                <button 
                  onClick={() => setShowAfterInLightbox(!showAfterInLightbox)}
                  className="mt-3 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                >
                  Voir {showAfterInLightbox ? 'AVANT' : 'APRÈS'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default BeforeAfter;
